// ── Inbox — Email card UI + send/schedule actions ────────────
//
// Owns the email-card expand/collapse state, the popout modal, the
// inline TipTap reply editor lifecycle, and the schedule-send flows
// (both inline-reply schedule and standalone composer schedule).
//
// State owned by the factory closure:
//   - `_expandedEmails` — Set of msgIds currently expanded in the
//     chat view. The bubble-renderer reads this when building cards.
//   - `_inlineEditors` — keyed by msgId, holds the TipTap editor
//     instance for each inline reply currently open. Destroyed on
//     cancel or successful send.
//
// State passed in via callbacks:
//   - `_activeClientId`, `_activeMessages`, `_clients` (read)
//   - `_pendingScheduled` (mutated via pushScheduled callback)
//   - `_emailEditor` (the standalone composer editor; read + cleared)
//   - `renderChatMessages`, `clearEmailAttachments` (re-render
//     triggers + composer-state reset)
//
// The email card's own buttons (open or close it, pop it out, reply) are
// answered by the Inbox thread's listener; the inline reply and the popout
// window still call their `window.*` handlers by name. The factory returns
// the methods so the parent assigns them. The card's element id carries the
// thread's prefix: `inbox-email-card-<id>` on the Inbox page.
//
// Note: `_resolveReplyToInPlace` is intentionally left in inbox.js —
// it's generic message-reply hydration (used by realtime.js too), not
// email-specific.

import { getSupabase } from '../config.js';
import { escHtml, linkifyHtml, toLocalDateTimeInput } from './format-helpers.js';
import { createEmailEditor, injectEditorCSS } from '../email-editor.js';
import { showToast } from '../utils.js';
import { whyNotSent, notSent, sayWhy, notSentTo, wordsToastOptions } from '../send-failure.js';

/**
 * A plain-text draft as the email editor's paragraphs: a blank line starts a
 * paragraph, a single line break stays a line break. Escaped first (a draft
 * is the coach's words, but it goes in as HTML).
 * @param {string} text
 * @returns {string}
 */
export function draftToEmailHtml(text) {
  return String(text || '')
    .split(/\n\s*\n/)
    .map((para) => `<p>${escHtml(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Build the email-send controller bound to inbox.js's state.
 *
 * @param {object} deps
 * @param {() => string | null} deps.getActiveClientId
 * @param {() => any[]} deps.getActiveMessages
 * @param {() => any[]} deps.getClients
 * @param {() => any | null} deps.getEmailEditor — standalone composer TipTap instance
 * @param {() => Set<string>} deps.getExpandedEmails
 * @param {() => Record<string, any>} deps.getInlineEditors
 * @param {(s: any) => void} deps.pushScheduled — append a scheduled entry to _pendingScheduled
 * @param {() => void} deps.renderChatMessages
 * @param {() => void} deps.clearEmailAttachments
 */
export function createEmailSend(deps) {
  const {
    getActiveClientId,
    getActiveMessages,
    getClients,
    getEmailEditor,
    getExpandedEmails,
    getInlineEditors,
    pushScheduled,
    renderChatMessages,
    clearEmailAttachments,
  } = deps;

  // ── Email card expand/collapse ──────────────────────────────
  function toggleEmailCard(msgId) {
    const expandedEmails = getExpandedEmails();
    const wasExpanded = expandedEmails.has(msgId);
    if (wasExpanded) expandedEmails.delete(msgId);
    else expandedEmails.add(msgId);
    // Re-render to apply expanded state cleanly
    renderChatMessages();
    // Scroll card into view when expanding
    if (!wasExpanded) {
      setTimeout(() => {
        const card = document.getElementById('inbox-email-card-' + msgId)?.closest('.inbox-ecard');
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  // ── Email popout modal ─────────────────────────────────────
  function popoutEmail(msgId) {
    const msg = getActiveMessages().find(m => m.id === msgId);
    if (!msg) return;
    const subject = msg.subject || 'No subject';
    const senderName = msg.sender_role === 'coach' ? 'You' : (msg.sender_name || 'Client');
    const direction = msg.direction === 'inbound' ? 'Received' : 'Sent';
    const dt = new Date(msg.created_at);
    const timeStr = dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

    const overlay = document.createElement('div');
    overlay.className = 'inbox-email-modal-overlay';
    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
    overlay.innerHTML = `
      <div class="inbox-email-modal">
        <div class="inbox-email-modal-header">
          <div class="inbox-email-modal-subject">${escHtml(subject)}</div>
          <button class="inbox-email-modal-close" onclick="this.closest('.inbox-email-modal-overlay').remove()">&times;</button>
        </div>
        <div class="inbox-email-modal-meta">
          <span class="inbox-ch-badge inbox-ch--email">Email</span>
          <strong>${escHtml(senderName)}</strong>
          <span>${direction} · ${timeStr}</span>
        </div>
        <div class="inbox-email-modal-body">${linkifyHtml(msg.body)}</div>
        <div class="inbox-email-modal-footer">
          <button class="inbox-email-modal-reply-btn" data-owner-only onclick="window._replyToEmail('${msgId}');this.closest('.inbox-email-modal-overlay').remove()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 00-4-4H4"/></svg>
            Reply
          </button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
  }

  // ── Reply to email ─────────────────────────────────────────
  function replyToEmail(msgId) {
    const msg = getActiveMessages().find(m => m.id === msgId);
    if (!msg) return;

    // Make sure the card is expanded
    const expandedEmails = getExpandedEmails();
    if (!expandedEmails.has(msgId)) {
      expandedEmails.add(msgId);
      renderChatMessages();
    }

    // Check if inline reply already exists
    const existing = document.getElementById('ecard-reply-' + msgId);
    if (existing) { existing.querySelector('textarea')?.focus(); return; }

    // Build quoted thread
    const senderName = msg.sender_role === 'coach' ? 'You' : (msg.sender_name || 'Client');
    const dt = new Date(msg.created_at);
    const dateStr = dt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

    const origSubject = msg.subject || '';
    const replySubject = origSubject.startsWith('Re:') ? origSubject : 'Re: ' + origSubject;

    // Insert inline reply editor into the card footer
    const footer = document.getElementById('inbox-email-card-' + msgId)?.closest('.inbox-ecard')?.querySelector('.inbox-ecard-footer');
    if (!footer) return;

    // The subject sits in a double-quoted value="" attribute. It used to get
    // a backslash before every apostrophe, which the coach then saw and sent.
    const safeSubject = escHtml(replySubject);
    const activeClientId = getActiveClientId();
    const client = getClients().find(c => c.id === activeClientId);
    const clientEmail = client?.email || '';
    const clientName = client?.name || client?.data?.name || '';
    // ⚠️ ESCAPED: this goes into innerHTML below, and a client types their
    // own name and email. Same as the email card's own To: line (inbox.js).
    const toLine = clientEmail ? `${escHtml(clientName)} &lt;${escHtml(clientEmail)}&gt;` : escHtml(clientName);

    footer.innerHTML = `
      <div class="ecard-inline-reply" id="ecard-reply-${msgId}">
        <div class="ecard-inline-reply-fields">
          <div class="ecard-inline-reply-field">
            <span class="ecard-inline-reply-label">To:</span>
            <span style="color:var(--text);font-size:12px;">${toLine}</span>
            <div style="margin-left:auto;display:flex;gap:6px;">
              <button class="ee-cc-toggle" onclick="this.closest('.ecard-inline-reply').querySelector('.ecard-cc-row').classList.toggle('hidden')">CC</button>
              <button class="ee-cc-toggle" onclick="this.closest('.ecard-inline-reply').querySelector('.ecard-bcc-row').classList.toggle('hidden')">BCC</button>
            </div>
          </div>
          <div class="ecard-inline-reply-field ecard-cc-row hidden">
            <span class="ecard-inline-reply-label">CC:</span>
            <input type="text" class="ecard-inline-reply-input" id="ecard-reply-cc-${msgId}" placeholder="Add CC recipients" />
          </div>
          <div class="ecard-inline-reply-field ecard-bcc-row hidden">
            <span class="ecard-inline-reply-label">BCC:</span>
            <input type="text" class="ecard-inline-reply-input" id="ecard-reply-bcc-${msgId}" placeholder="Add BCC recipients" />
          </div>
          <div class="ecard-inline-reply-field">
            <span class="ecard-inline-reply-label">Subject:</span>
            <input type="text" class="ecard-inline-reply-input" id="ecard-reply-subject-${msgId}" value="${safeSubject}" />
          </div>
        </div>
        <div id="ecard-reply-editor-${msgId}"></div>
        <div class="ecard-inline-reply-actions">
          <button class="inbox-ecard-reply-btn" data-owner-only onclick="window._sendInlineReply('${msgId}')">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            Send
          </button>
          <button class="inbox-email-schedule-btn" onclick="window._showInlineScheduleSend('${msgId}')" title="Schedule send">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </button>
          <button class="ecard-inline-reply-discard" onclick="window._cancelInlineReply('${msgId}')">Discard</button>
        </div>
      </div>`;

    // Initialize TipTap for inline reply
    setTimeout(async () => {
      injectEditorCSS();
      // Escape FIRST, then turn line breaks into <br>. The old order added
      // the <br>s and then escaped them, so every quoted line break showed as
      // the text "<br>", and an & was left as it was.
      const quotedHtml = '<p></p><br><p style="color:#9ca3af;font-size:12px;">On ' + escHtml(dateStr) + ', ' + escHtml(senderName) + ' wrote:</p><blockquote style="border-left:3px solid #e5e7eb;padding-left:10px;color:#6b7280;margin:4px 0;">' + escHtml(msg.body).replace(/\n/g, '<br>') + '</blockquote>';
      const ed = await createEmailEditor('ecard-reply-editor-' + msgId, { placeholder: 'Type your reply...', content: quotedHtml });
      if (ed) {
        getInlineEditors()[msgId] = ed;
        ed.focus();
      }
      footer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }

  // ── Send inline reply ──────────────────────────────────────
  async function sendInlineReply(msgId) {
    const inlineEditors = getInlineEditors();
    const ed = inlineEditors[msgId];
    const body = ed ? ed.getText().trim() : '';
    const html = ed ? ed.getHTML() : '';
    const subject = document.getElementById('ecard-reply-subject-' + msgId)?.value?.trim() || '';
    const activeClientId = getActiveClientId();
    if (!body || !activeClientId) return;

    const replyEl = document.getElementById('ecard-reply-' + msgId);
    const btn = replyEl?.querySelector('.inbox-ecard-reply-btn');
    // The button holds an icon as well as its word; it comes back exactly as it was.
    const btnHtml = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }

    // ⚠️ A FAILED REPLY SAYS WHY (spec §15.6). This used to turn the button back
    // on and nothing else, so a refused email ("Client has no email on file")
    // looked sent. Now it says why the way every other Inbox send does
    // (send-failure.js), and the reply box and its words are left as they were.
    // Only the request sits inside the try: a sent email is never reported as
    // failed because something after it went wrong.
    let res;
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      res = await fetch('/.netlify/functions/messages-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ clientId: activeClientId, body, html, senderRole: 'coach', channel: 'email', subject }),
      });
      if (!res.ok) throw notSent(await whyNotSent(res));
    } catch (err) {
      if (btn) { btn.disabled = false; btn.innerHTML = btnHtml; }
      const why = sayWhy(err);
      if (getActiveClientId() === activeClientId) {
        showToast(why, 'error');
      } else {
        // ⚠️ ANOTHER CLIENT IS OPEN NOW: their thread replaced this reply box, so
        // the toast names who the email was for and carries the words, with Copy
        // message (as the Inbox's main composer does).
        const client = getClients().find(c => c.id === activeClientId);
        const who = client?.name || client?.data?.name || '';
        showToast(notSentTo({ who, kind: 'email', why, words: body }), 'error', wordsToastOptions(body));
      }
      return;
    }
    const json = await res.json().catch(() => ({}));
    const activeMessages = getActiveMessages();
    if (json.message && !activeMessages.some(m => m.id === json.message.id)) {
      activeMessages.push(json.message);
    }
    // Clean up editor and close inline reply
    if (inlineEditors[msgId]) { inlineEditors[msgId].destroy(); delete inlineEditors[msgId]; }
    getExpandedEmails().delete(msgId);
    renderChatMessages();
  }

  // ── Schedule the inline reply (per-card schedule flow) ──────
  function showInlineScheduleSend(msgId) {
    const ed = getInlineEditors()[msgId];
    const body = ed ? ed.getText().trim() : '';
    const html = ed ? ed.getHTML() : '';
    const subject = document.getElementById('ecard-reply-subject-' + msgId)?.value?.trim() || '';
    const activeClientId = getActiveClientId();
    if (!body || !activeClientId) { alert('Compose a reply first.'); return; }

    const now = new Date();
    const defaultDate = new Date(now.getTime() + 60 * 60 * 1000);
    const dateStr = toLocalDateTimeInput(defaultDate);

    const overlay = document.createElement('div');
    overlay.className = 'inbox-email-modal-overlay';
    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
    overlay.innerHTML = `
      <div class="inbox-email-modal" style="max-width:380px;">
        <div class="inbox-email-modal-header">
          <div class="inbox-email-modal-subject">Schedule Reply</div>
          <button class="inbox-email-modal-close" onclick="this.closest('.inbox-email-modal-overlay').remove()">&times;</button>
        </div>
        <div style="padding:20px;">
          <p style="font-size:13px;color:var(--text-muted);margin-bottom:14px;">Choose when this reply should be sent.</p>
          <input type="datetime-local" id="schedule-inline-datetime" value="${dateStr}" style="width:100%;padding:10px;border:1px solid var(--border);border-radius:6px;font-size:14px;color:var(--text);background:var(--bg-hover);" />
          <div style="display:flex;gap:8px;margin-top:16px;">
            <button class="btn-primary" style="flex:1;padding:10px;font-size:13px;font-weight:700;" id="schedule-inline-confirm">Schedule</button>
            <button class="inbox-email-discard-btn" style="flex:1;padding:10px;font-size:13px;" onclick="this.closest('.inbox-email-modal-overlay').remove()">Cancel</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    document.getElementById('schedule-inline-confirm')?.addEventListener('click', async () => {
      const scheduledFor = new Date(document.getElementById('schedule-inline-datetime').value).toISOString();
      try {
        const supabase = getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch('/.netlify/functions/scheduled-email-create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ clientId: activeClientId, subject, body, html, scheduledFor }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        overlay.remove();
        cancelInlineReply(msgId);
        // Add to inline scheduled immediately
        pushScheduled({ id: json.id, clientId: activeClientId, subject, body, channel: 'email', scheduledFor, status: 'pending' });
        renderChatMessages();
        const dt = new Date(scheduledFor);
        const timeLabel = dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
        showToast('Reply scheduled for ' + timeLabel, 'success');
      } catch (err) { alert('Failed: ' + err.message); }
    });
  }

  function cancelInlineReply(msgId) {
    const inlineEditors = getInlineEditors();
    if (inlineEditors[msgId]) { inlineEditors[msgId].destroy(); delete inlineEditors[msgId]; }
    const card = document.getElementById('inbox-email-card-' + msgId)?.closest('.inbox-ecard');
    const footer = card?.querySelector('.inbox-ecard-footer');
    if (footer) {
      footer.innerHTML = `<button type="button" class="inbox-ecard-reply-btn" data-owner-only data-action="reply-email" data-mid="${escHtml(msgId)}">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 00-4-4H4"/></svg>
        Reply
      </button>`;
    }
  }

  // ── Discard standalone email composer draft ─────────────────
  function discardEmailDraft() {
    const subjectField = document.getElementById('uc-email-subject');
    if (subjectField) subjectField.value = '';
    const ed = getEmailEditor();
    if (ed) ed.clear();
    clearEmailAttachments();
    window._setComposerChannel?.('in_app');
  }

  // ── Schedule the standalone composer email ──────────────────
  function showScheduleSend() {
    const now = new Date();
    const defaultDate = new Date(now.getTime() + 60 * 60 * 1000);
    const dateStr = toLocalDateTimeInput(defaultDate);

    const overlay = document.createElement('div');
    overlay.className = 'inbox-email-modal-overlay';
    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
    overlay.innerHTML = `
      <div class="inbox-email-modal" style="max-width:380px;">
        <div class="inbox-email-modal-header">
          <div class="inbox-email-modal-subject">Schedule Send</div>
          <button class="inbox-email-modal-close" onclick="this.closest('.inbox-email-modal-overlay').remove()">&times;</button>
        </div>
        <div style="padding:20px;">
          <p style="font-size:13px;color:var(--text-muted);margin-bottom:14px;">Choose when this message should be sent.</p>
          <input type="datetime-local" id="schedule-send-datetime" value="${dateStr}" style="width:100%;padding:10px;border:1px solid var(--border);border-radius:6px;font-size:14px;color:var(--text);background:var(--bg-card);" />
          <div style="display:flex;gap:8px;margin-top:16px;">
            <button class="btn-primary" style="flex:1;padding:10px;font-size:13px;font-weight:700;" onclick="window._confirmScheduleSend()">Schedule</button>
            <button class="inbox-email-discard-btn" style="flex:1;padding:10px;font-size:13px;" onclick="this.closest('.inbox-email-modal-overlay').remove()">Cancel</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(overlay);
  }

  async function confirmScheduleSend() {
    const input = document.getElementById('schedule-send-datetime');
    if (!input?.value) return;

    const scheduledFor = new Date(input.value).toISOString();
    const subject = document.getElementById('uc-email-subject')?.value?.trim() || '';
    const ed = getEmailEditor();
    const body = ed ? ed.getText().trim() : '';
    const html = ed ? ed.getHTML() : '';
    const activeClientId = getActiveClientId();

    if (!body || !activeClientId) { alert('Please compose an email first.'); return; }

    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/.netlify/functions/scheduled-email-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ clientId: activeClientId, subject, body, html, scheduledFor }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);

      // Close modal, clear composer, show toast
      document.querySelector('.inbox-email-modal-overlay')?.remove();
      if (ed) ed.clear();
      clearEmailAttachments();
      const subjectField = document.getElementById('uc-email-subject');
      if (subjectField) subjectField.value = '';

      // Add to inline scheduled immediately
      pushScheduled({ id: json.id, clientId: activeClientId, subject, body, channel: 'email', scheduledFor, status: 'pending' });
      renderChatMessages();

      const dt = new Date(scheduledFor);
      const timeLabel = dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      showToast('Email scheduled for ' + timeLabel, 'success');
    } catch (err) {
      alert('Failed to schedule: ' + err.message);
    }
  }

  // ── A draft carried in from the corner ("Write email", "Open in Inbox") ──
  // It fills the composer's email editor ONCE: when the editor is there, or
  // the moment it is made (it is made the first time Email is chosen). Never
  // over words already in the editor, and a newer conversation's open drops a
  // draft still waiting (carryDraftIntoEditor(null)).
  /** @type {string | null} */
  let _pendingDraft = null;

  /** @param {string | null | undefined} text */
  function carryDraftIntoEditor(text) {
    _pendingDraft = typeof text === 'string' && text.trim() ? text : null;
    return applyPendingDraft();
  }

  /** @returns {boolean} whether the editor took the draft now */
  function applyPendingDraft() {
    const ed = getEmailEditor();
    if (!ed || _pendingDraft == null) return false;
    const text = _pendingDraft;
    _pendingDraft = null;
    if (ed.getText().trim()) return false;
    ed.setContent(draftToEmailHtml(text));
    return true;
  }

  return {
    carryDraftIntoEditor,
    applyPendingDraft,
    toggleEmailCard,
    popoutEmail,
    replyToEmail,
    sendInlineReply,
    showInlineScheduleSend,
    cancelInlineReply,
    discardEmailDraft,
    showScheduleSend,
    confirmScheduleSend,
  };
}
